import { NodeApi } from 'react-arborist';
import { BundleTreeTypes } from '@repo/atomic-components';
import { BundleSectionsTreeNode } from '@/api';

// Walk up from a node until we reach the provider it belongs to. Returns
// the provider node itself when called on one, or null if the node isn't
// under a provider (e.g. it's the synthetic root).
const findProviderAncestor = (node: NodeApi<BundleSectionsTreeNode> | null): NodeApi<BundleSectionsTreeNode> | null => {
  let cursor: NodeApi<BundleSectionsTreeNode> | null = node;
  while (cursor && !cursor.isRoot) {
    if (cursor.data?.type === BundleTreeTypes.PROVIDER) return cursor;
    cursor = cursor.parent;
  }
  return null;
};

/**
 * Drag-and-drop rules for BundleSectionsTree.
 *
 * The backend enforces certain structural invariants (e.g. only providers
 * live at the root, categories live under providers, etc.). Repeating those
 * invariants here as pre-drop guards keeps the UI in step with the API and
 * gives the user immediate feedback (drop cursor doesn't appear where the
 * drop would be rejected) rather than the drop succeeding visually and
 * then getting rolled back after a failed request.
 *
 * Each rule takes a single dragged node type and the proposed drop target
 * (its parent node in the tree — root = null) and returns true when that
 * combination is forbidden. `applyRules` then loops over the multi-drag
 * selection and forbids the drop if ANY dragged item is forbidden.
 */

interface DropContext {
  // The tree node that would become the parent of the dropped item. When
  // dropping at the root (between top-level providers) react-arborist
  // passes the tree's synthetic root node, whose `isRoot` is true and whose
  // `.data` is not a real domain node — check `isRoot` first, never
  // dereference `.data.type` on it.
  parent: NodeApi<BundleSectionsTreeNode>;
  // Drop slot within `parent`. When a Provider is expanded and the user
  // aims between its last child and the next Provider row, react-arborist
  // reports the *previous provider* as the parent with `index === children
  // length` — a valid "just after this provider" position. Rules need
  // access to this so they can distinguish "inside a provider" from "at a
  // provider boundary".
  index: number;
}

type DragRule = (draggedNode: NodeApi<BundleSectionsTreeNode>, context: DropContext) => boolean;

// Providers may only live at the root level. This is trickier than "parent
// must be root" because dropping just below the last child of an expanded
// provider makes react-arborist report that provider as the parent (with
// index = children.length). That's really a "just after this provider at
// root" drop, so we allow it. Only reject drops that would put the
// provider *inside* a category / sub-category / cluster or between two
// existing children of a provider.
const providerMustStayAtRoot: DragRule = (draggedNode, { parent, index }) => {
  if (draggedNode.data.type !== BundleTreeTypes.PROVIDER) return false;
  if (parent.isRoot) return false;
  const childCount = parent.children?.length ?? 0;
  // Boundary slot — treat as "at root, after this provider".
  if (parent.data?.type === BundleTreeTypes.PROVIDER && index >= childCount) return false;
  return true;
};

// Categories are tied to a single provider on the backend — you can
// reorder them within their provider, and you can nest one into another
// category in the same provider (see rule below), but you can't move them
// to a different provider or out to the root. Compare provider ancestors
// on both sides of the drop.
const categoryStaysInsideItsProvider: DragRule = (draggedNode, { parent }) => {
  if (draggedNode.data.type !== BundleTreeTypes.CATEGORY) return false;
  const sourceProvider = findProviderAncestor(draggedNode.parent);
  const targetProvider = findProviderAncestor(parent);
  if (!sourceProvider || !targetProvider) return true;
  return sourceProvider.id !== targetProvider.id;
};

// A category may be nested inside another category (effectively becoming
// a sub-category of it) — but only when the dragged category itself
// doesn't already contain sub-categories. Nesting a category-with-subs
// under another category would push the sub-cats down a level (turning
// them into sub-sub-cats), which the backend doesn't support.
const categoryCanOnlyNestIfItHasNoSubCategories: DragRule = (draggedNode, { parent }) => {
  if (draggedNode.data.type !== BundleTreeTypes.CATEGORY) return false;
  if (parent.data?.type !== BundleTreeTypes.CATEGORY) return false;
  const draggedChildren = draggedNode.children ?? [];
  const draggedHasSubCategories = draggedChildren.some((child) => child.data.type === BundleTreeTypes.SUB_CATEGORY);
  return draggedHasSubCategories;
};

// Sub-categories are also tied to a single provider. They may either be
// reordered within their current category, or promoted to a category at
// the provider level (handled in handleMove as "sub-cat → cat"). Anything
// else — moving to a different provider, out to the root, or nesting
// under another sub-category / cluster — is forbidden.
const subCategoryStaysInsideItsProvider: DragRule = (draggedNode, { parent }) => {
  if (draggedNode.data.type !== BundleTreeTypes.SUB_CATEGORY) return false;
  const sourceProvider = findProviderAncestor(draggedNode.parent);
  const targetProvider = findProviderAncestor(parent);
  if (!sourceProvider || !targetProvider) return true;
  return sourceProvider.id !== targetProvider.id;
};

// A sub-category may be placed among categories (turning it into a
// category), reordered within its current parent category, or moved
// into a different category in the same provider. Reject anything else
// — dropping under a sub-category / cluster / document.
const subCategoryValidTargets: DragRule = (draggedNode, { parent }) => {
  if (draggedNode.data.type !== BundleTreeTypes.SUB_CATEGORY) return false;
  if (!parent.data) return true;
  const parentType = parent.data.type;
  if (parentType === BundleTreeTypes.CATEGORY) return false;
  if (parentType === BundleTreeTypes.PROVIDER) return false;
  return true;
};

// Categories may contain a mix of sub-categories and clusters, but the
// sub-categories must always come first — they can't be dropped below or
// between clusters. Rule: every child strictly above the drop slot must
// be either a sub-category or the dragged item itself. As soon as a
// non-sub-cat sibling sits above the drop point, the drop is illegal.
const subCategoryStaysAboveClusters: DragRule = (draggedNode, { parent, index }) => {
  if (draggedNode.data.type !== BundleTreeTypes.SUB_CATEGORY) return false;
  if (parent.data?.type !== BundleTreeTypes.CATEGORY) return false;
  const children = parent.children ?? [];
  for (let i = 0; i < index && i < children.length; i++) {
    const sibling = children[i];
    if (sibling.id === draggedNode.id) continue;
    if (sibling.data.type !== BundleTreeTypes.SUB_CATEGORY) return true;
  }
  return false;
};

// Clusters live inside a category or sub-category and must stay within
// their current provider. Reject drops that land them anywhere else —
// including directly on a provider row, at the root level, or in the
// gap between categories where the parent would be their provider.
const clusterStaysInsideItsProvider: DragRule = (draggedNode, { parent }) => {
  if (draggedNode.data.type !== BundleTreeTypes.CLUSTER) return false;
  const sourceProvider = findProviderAncestor(draggedNode.parent);
  const targetProvider = findProviderAncestor(parent);
  if (!sourceProvider || !targetProvider) return true;
  return sourceProvider.id !== targetProvider.id;
};

// Clusters must sit inside a category or sub-category — never at the
// root, never as a direct child of a provider (which would put them
// between categories in the tree). Only Category and Sub-category
// parents are valid drop targets.
const clusterValidTargets: DragRule = (draggedNode, { parent }) => {
  if (draggedNode.data.type !== BundleTreeTypes.CLUSTER) return false;
  if (!parent.data) return true;
  const parentType = parent.data.type;
  if (parentType === BundleTreeTypes.CATEGORY) return false;
  if (parentType === BundleTreeTypes.SUB_CATEGORY) return false;
  return true;
};

// When dropping a cluster into a category that also contains sub-cats,
// the cluster must sit below all of them (sub-cats always come first in
// a category — same invariant enforced from the sub-cat side).
//
// One deliberate exception: dropping directly onto the category row
// (react-arborist reports `index === 0`) means "make me the first
// cluster in this category". handleMove translates that back to the
// post-sub-cat position before splicing, so it's allowed here.
const clusterStaysBelowSubCategories: DragRule = (draggedNode, { parent, index }) => {
  if (draggedNode.data.type !== BundleTreeTypes.CLUSTER) return false;
  if (parent.data?.type !== BundleTreeTypes.CATEGORY) return false;
  const children = parent.children ?? [];
  const filtered = children.filter((c) => c.id !== draggedNode.id);
  const subCatCount = filtered.filter((c) => c.data.type === BundleTreeTypes.SUB_CATEGORY).length;
  if (subCatCount === 0) return false;
  if (index === 0) return false; // Drop-on-header: interpreted as "first cluster".
  return index < subCatCount;
};

// Central rule registry — add new rules here as the backend adds more
// structural invariants. Order doesn't matter (any true short-circuits).
const RULES: DragRule[] = [
  providerMustStayAtRoot,
  categoryStaysInsideItsProvider,
  categoryCanOnlyNestIfItHasNoSubCategories,
  subCategoryStaysInsideItsProvider,
  subCategoryValidTargets,
  subCategoryStaysAboveClusters,
  clusterStaysInsideItsProvider,
  clusterValidTargets,
  clusterStaysBelowSubCategories,
];

export const isDropForbidden = (
  dragNodes: NodeApi<BundleSectionsTreeNode>[],
  parent: NodeApi<BundleSectionsTreeNode>,
  index: number,
): boolean => {
  const context: DropContext = { parent, index };
  return dragNodes.some((dragged) => RULES.some((rule) => rule(dragged, context)));
};
